import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';

import React, { useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { testId } from 'ui-components/services/testid-service';

import { AutoCompleteField } from '../../../../constants/autocomplete-field';
import { CREDIT_CARD_FIELDS, MONTHS } from '../../constants';
import { useCreditCardContext } from '../../credit-card-context-provider';
import { messages } from '../../message';
import { useCreditCardField } from '../../use-credit-card-field';

import commonStyle from '../../style.module.css';
import style from './style.module.less';

export const CreditCardMonth = ({ ignoreError = false }) => {
  const { disabled, showAllErrors } = useCreditCardContext();
  const { value, onChange, onBlur, error, touched } = useCreditCardField(CREDIT_CARD_FIELDS.CARD_EXPIRATION_MONTH);

  const intl = useIntl();
  const shouldShowErrors = !ignoreError && (touched || showAllErrors);
  const errorMessage = shouldShowErrors && error ? error.message : '';

  const togglerMessage = useMemo(() => {
    const selectedMonth = MONTHS.find((month) => month.value === value);

    const messageDescriptor = selectedMonth
      ? {
          ...messages.monthDropdownEntry,
          values: {
            monthDate: new Date(null, selectedMonth.index),
          },
        }
      : messages.monthDropdownEntryDefault;

    return intl.formatMessage(messageDescriptor, messageDescriptor.values);
  }, [value, intl]);

  const onInputChange = useCallback(
    (event) => {
      const newValue = Number(event?.target?.value);
      const newMonth = MONTHS.find((month) => month.value === newValue);
      if (newMonth) {
        onChange(newValue);
      } else {
        onChange(undefined);
      }
    },
    [onChange],
  );

  const _onBlur = useCallback(() => {
    onBlur(value);
  }, [onBlur, value]);

  return (
    <div className={style.container} {...testId('card-provider-dropdown')}>
      <DropdownMenu
        className={style['dropdown-menu']}
        disabled={disabled}
        dropdownMenuShouldHaveWidthOfButton
        hasError={!!errorMessage}
        value={value}
        variant='form'
      >
        <DropdownMenuToggler className={style['dropdown-toggler']} onBlur={_onBlur} tabIndex={-1}>
          <input
            autoComplete={AutoCompleteField.CCExpMonth}
            className={style['month-input']}
            id='cc-exp-month'
            name='cc-exp-month'
            onChange={onInputChange}
            value={togglerMessage}
          />
        </DropdownMenuToggler>
        {MONTHS.map((month) => {
          const message = intl.formatMessage(messages.monthDropdownEntry, {
            monthDate: new Date(null, month.index),
          });
          return (
            <DropdownMenuItem key={month.value} onClick={() => onChange(month.value)} selected={value === month.value}>
              {message}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenu>
      {errorMessage && (
        <div className={commonStyle['error-prompt']}>
          <FontAwesomeIcon className={commonStyle['error-icon']} icon={faExclamationCircle} />
          {errorMessage}
        </div>
      )}
    </div>
  );
};

CreditCardMonth.propTypes = {
  ignoreError: PropTypes.bool,
};
